import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import {
  Loader, Breadcrumbs, SectionRenderer, ProsCons, VerdictCard, FAQ, ToolLogo,
  Rating, PricingBadge, ToolCard, ArticleCard, Overline, LazyImage, Chip, ExternalLink, formatDate,
} from "@/components/shared";
import { AdSlot } from "@/components/Ads";

export default function ToolDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.tool(slug).then(setD).catch(() => setD(false)); }, [slug]);
  useSEO(d && d.tool ? { title: d.tool.seo_title, description: d.tool.meta_description } : {});
  if (d === false) return <NotFound />;
  if (!d) return <Loader />;
  const t = d.tool;

  return (
    <div>
      <div className="klyroo-grid border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "AI Tools", to: "/tools" }, { label: t.category, to: `/tools` }, { label: t.name }]} />
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <ToolLogo name={t.name} logo={t.logo} size={76} />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <Overline>{t.category} · {t.subcategory}</Overline>
                <PricingBadge model={t.pricing_model} />
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight">{t.name}</h1>
              <p className="text-slate-300 mt-3 text-lg max-w-2xl">{t.tagline}</p>
              <div className="flex flex-wrap items-center gap-4 mt-5">
                <Rating value={t.rating} className="text-base" />
                <span className="text-sm text-slate-400 font-mono">{t.price}</span>
                <a href={t.website} target="_blank" rel="noopener noreferrer" data-testid="visit-website"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white transition-colors">
                  Visit {t.name} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_260px] gap-10">
        <article>
          <div className="rounded-2xl overflow-hidden mb-8"><LazyImage src={t.image} alt={t.image_alt} ratio="16/9" /></div>
          <SectionRenderer sections={(t.body_sections || []).filter((s) => s.heading !== "KLYROO Verdict")} />
          <ProsCons pros={t.pros || []} cons={t.cons || []} />
          {t.verdict && <VerdictCard score={t.verdict.score} summary={t.verdict.summary} />}
          <FAQ faqs={t.faqs || []} />
        </article>

        <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
          <div className="card-surface rounded-2xl p-5">
            <Overline className="mb-3">Quick Facts</Overline>
            <Fact label="Category" value={t.category.replace("AI ", "")} />
            <Fact label="Pricing" value={t.pricing_model} />
            <Fact label="Price" value={t.price} />
            <Fact label="Rating" value={`${t.rating}/10`} />
            <Fact label="Updated" value={formatDate(t.updated_at)} last />
          </div>
          <div className="card-surface rounded-2xl p-5">
            <Overline className="mb-3">Best For</Overline>
            <div className="flex flex-wrap gap-2">{t.best_for.map((b) => <Chip key={b}>{b}</Chip>)}</div>
          </div>
          <AdSlot placement="sidebar" />
        </aside>
      </div>

      {(d.related_tools?.length > 0 || d.related_articles?.length > 0) && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          {d.related_tools?.length > 0 && (
            <section className="mb-12">
              <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Alternatives & Related Tools</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{d.related_tools.map((rt) => <ToolCard key={rt.slug} t={rt} />)}</div>
            </section>
          )}
          {d.related_articles?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Related Reading</h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{d.related_articles.slice(0, 3).map((a) => <ArticleCard key={a.slug} a={a} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, last }) {
  return (
    <div className={`flex justify-between gap-3 py-2.5 ${last ? "" : "border-b border-white/5"}`}>
      <span className="text-sm text-slate-400">{label}</span>
      <span className="text-sm text-white font-medium text-right">{value}</span>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <div className="font-display text-6xl font-extrabold text-white">404</div>
      <p className="text-slate-400 mt-3">This page could not be found.</p>
      <Link to="/" className="mt-6 px-5 py-2.5 rounded-lg bg-klyroo-cyan text-black font-semibold">Back to KLYROO</Link>
    </div>
  );
}
